import { ChipProps } from "@/components/Chip/types";
import type { ProviderCookieValue } from "@/containers/PaymentContainer/PaymentProviderFactory/types";
import { UserContextProps } from "@/contexts/UserContext/types";
import { I_PaymentPage, I_PrincingPage } from "@/data/types/pages";
import { PlanProps } from "@/types/plan";
import type { UserProps } from "@/types/user";
import { RichtextItem, SubscriptionPlan } from "@/types/types";
import type { PricingExperimentTreatment } from "@/sites/types";
import type { PaymentFailure } from "@/shared/payment/errors";
import { CheckoutPricingSummaryProps } from "./CheckoutPricingSummary";

export interface PricingExperimentContainerState {
  treatment: PricingExperimentTreatment;
  planId?: string;
  isPendingClientResolution: boolean;
  chipLabels: Partial<Record<PricingExperimentTreatment, string>>;
}

export interface CouponProps {
  code: string;
  isValid: boolean;
  errorMessage?: string;
}
export interface ISubscriptionPlansContainer {
  /** Pricing page data */
  data: I_PrincingPage;
  /** Plans data */
  plans: PlanProps[];
  /** Coupon data */
  coupon?: CouponProps;
  /** Viewer's country (from `cloudfront-viewer-country`), used to resolve the card trust strip market. */
  country?: string;
  /** Origin URL */
  origin?: string;
  /** Payment texts */
  paymentTexts?: I_PaymentPage;
  /** SSR bootstrap (+ payment cutover): initial pick result from `prefetchPaymentBootstrap`. */
  initialProviderData?: ProviderCookieValue;
  /** Plan id the server-side bootstrap assigned before first paint. */
  serverAssignedPlanId?: string;
  /** Profile after SSR assign bootstrap; hydrates client user context once. */
  serverUser?: UserProps;
  /** True when SSR payment bootstrap failed (non-auth); UI uses CMS error copy only. */
  serverPaymentError?: boolean;
  /** The failed provider pick that stopped the SSR bootstrap, if any (QRWMAU-2593 telemetry). */
  serverPickFailure?: PaymentFailure<"pick">;
  /** True when the SSR payment bootstrap was resurrecting a cancelled subscription. */
  serverCancelledRenew?: boolean;
  /** Present only when the Amplitude pricing experiment can apply to this render. */
  pricingExperiment?: PricingExperimentContainerState;
}

export interface IPlansManager {
  data: I_PrincingPage;
  currentPlan: SubscriptionPlan;
  userContext: UserContextProps;
  coupon?: CouponProps;
  isMounted?: boolean;
  origin?: string;
}

export const CANCELLED_SUBSCRIPTION = "cancelledSubscription";
export interface PlansContainerProps {
  data: I_PrincingPage;
  currentPlan: SubscriptionPlan;
  onSelectPlan: (planId: string) => void;
  isStatusActive: boolean;
}

export type UnifiedCheckoutPricingProps = CheckoutPricingSummaryProps;

export interface PlanWrapperProps extends PlansContainerProps {
  customClass?: string;
  isUserLogged?: boolean;
  /** When set, renders the unified checkout pricing rows instead of plan cards. */
  unifiedCheckoutPricing?: UnifiedCheckoutPricingProps;
}

export interface PlanPriceProps {
  price: {
    richtext: RichtextItem | RichtextItem[];
  };
  priceWithDiscount?: string;
}

export interface PlanHeaderProps {
  id: string;
  name: {
    richtext: RichtextItem;
    chip?: string;
    chipColor?: ChipProps["color"];
  };
  isActive: boolean;
  isStatusActive: boolean;
}

export type SubscriptionPlansNotLoggedProps = PlansContainerProps;

export type SubscriptionPlansLoggedProps = PlansContainerProps;

export type PricingLoggedContainerProps = PlansContainerProps;

export type PricingContainerProps = PlansContainerProps;
